import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ADMIN_PROSPECTS_PAGE_SIZE,
  buildListAdminProspectAssociatedCampaignsRequest,
  listAdminProspectAssociatedCampaigns,
} from '../../services/admin/prospectsService';
import { createAdminProspectRequestGate } from '../../services/admin/prospectTargetingFilters';
import {
  classifyAdminCallableError,
  adminProspectListErrorKey,
} from './adminProspectDisplay';
import {
  associatedCampaignBuildStatusLabel,
  associatedCampaignDisplayName,
  associatedCampaignOpenPath,
  associatedCampaignStatusLabel,
  formatAssociatedCampaignCreatedAt,
  formatAssociatedCampaignIncludedAt,
} from './prospectAssociatedCampaignsDisplay';
import { runAssociatedCampaignsLoadLoop } from './prospectAssociatedCampaignsLoad.js';
import { isProspectLazyPanelVisible, shouldMountProspectLazyPanel } from './adminProspectDetailMobileSections';

/**
 * Read-only associated-campaigns disclosure for Immo 360.
 * Lazy-loads on first open; resets when prospectId changes.
 * Membership is stored target docs — not mailing/sent status.
 */
export default function ProspectAssociatedCampaignsSection({
  prospectId,
  language,
  t,
  mobileLayout = false,
  mobileActive = false,
  hideTitle = false,
  embedded = false,
}) {
  const id = String(prospectId || '').trim();
  const sectionId = useId();
  const panelId = `${sectionId}-panel`;
  const buttonId = `${sectionId}-toggle`;

  const [open, setOpen] = useState(false);
  const [mobileDisclosedOnce, setMobileDisclosedOnce] = useState(false);
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [orderingCoverage, setOrderingCoverage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [emptyWithMore, setEmptyWithMore] = useState(false);

  const gateRef = useRef(createAdminProspectRequestGate());
  const mountedRef = useRef(true);
  const prospectIdRef = useRef(id);
  const loadingMoreRef = useRef(false);
  const entriesRef = useRef(entries);

  prospectIdRef.current = id;
  entriesRef.current = entries;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    gateRef.current.next();
    setOpen(false);
    setMobileDisclosedOnce(false);
    setEntries([]);
    setStatus('idle');
    setErrorKind(null);
    setNextPageToken(null);
    setOrderingCoverage(null);
    setBusy(false);
    setLoadMoreFailed(false);
    setEmptyWithMore(false);
    loadingMoreRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!mobileLayout) return;
    if (!mobileActive) return;
    setMobileDisclosedOnce(true);
  }, [mobileLayout, mobileActive]);

  useEffect(() => {
    if (!mobileLayout && mobileDisclosedOnce && status !== 'idle') {
      setOpen(true);
    }
  }, [mobileLayout, mobileDisclosedOnce, status]);

  const loadCampaigns = useCallback(async ({ append = false, pageToken } = {}) => {
    if (!id) return;
    if (append && loadingMoreRef.current) return;
    const ownerProspectId = id;
    const requestId = gateRef.current.next();
    if (append) loadingMoreRef.current = true;
    setBusy(true);
    if (!append) {
      setStatus('loading');
      setErrorKind(null);
      setLoadMoreFailed(false);
      setEmptyWithMore(false);
    }

    const initialEntries = append ? [...entriesRef.current] : [];

    try {
      const result = await runAssociatedCampaignsLoadLoop({
        initialEntries,
        pageToken: pageToken || null,
        shouldAbort: () =>
          !mountedRef.current ||
          !gateRef.current.isCurrent(requestId) ||
          prospectIdRef.current !== ownerProspectId,
        fetchPage: async ({ pageToken: token }) =>
          listAdminProspectAssociatedCampaigns(
            buildListAdminProspectAssociatedCampaignsRequest({
              prospectId: ownerProspectId,
              pageSize: ADMIN_PROSPECTS_PAGE_SIZE,
              pageToken: token || undefined,
            }),
          ),
      });

      if (result.aborted) return;

      setEntries(result.entries);
      setNextPageToken(result.nextPageToken);
      setOrderingCoverage(result.orderingCoverage);
      setEmptyWithMore(Boolean(result.emptyWithMore));
      setLoadMoreFailed(false);
      setStatus(result.status);
    } catch (err) {
      if (
        !mountedRef.current ||
        !gateRef.current.isCurrent(requestId) ||
        prospectIdRef.current !== ownerProspectId
      ) {
        return;
      }
      setErrorKind(classifyAdminCallableError(err));
      if (append) {
        // Keep previously loaded entries after pagination failure.
        setEntries(initialEntries.length ? initialEntries : entriesRef.current);
        setLoadMoreFailed(true);
        setStatus('ready');
      } else {
        setEntries([]);
        setNextPageToken(null);
        setEmptyWithMore(false);
        setStatus('error');
      }
    } finally {
      if (append) loadingMoreRef.current = false;
      if (
        mountedRef.current &&
        gateRef.current.isCurrent(requestId) &&
        prospectIdRef.current === ownerProspectId
      ) {
        setBusy(false);
      }
    }
  }, [id]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && status === 'idle') {
      loadCampaigns();
    }
  };

  useEffect(() => {
    if (!mobileLayout || !mobileActive) return;
    if (status === 'idle') {
      loadCampaigns();
    }
  }, [mobileLayout, mobileActive, status, loadCampaigns]);

  const errorMessage = errorKind ? t(adminProspectListErrorKey(errorKind)) : t('admin.prospection.error.generic');
  const showContinue =
    Boolean(nextPageToken) && (status === 'ready' || emptyWithMore) && !loadMoreFailed;
  const showInternalToggle = !mobileLayout;
  const keepMounted = shouldMountProspectLazyPanel({
    mobileLayout,
    mobileActive,
    mobileDisclosedOnce,
    open,
    status,
  });
  const panelVisible = isProspectLazyPanelVisible({
    mobileLayout,
    mobileActive,
    mobileDisclosedOnce,
    open,
    status,
  });
  const chrome = embedded ? 'min-w-0' : 'rounded-xl border border-slate-200 bg-white p-4';

  return (
    <section className={chrome} data-embedded={embedded ? 'true' : 'false'}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {!hideTitle ? (
            <h2 className="text-sm font-semibold text-slate-800">
              {t('admin.prospection.associatedCampaigns.title')}
            </h2>
          ) : null}
          <p className={`${hideTitle ? 'mt-0' : 'mt-1'} text-xs text-slate-500`}>
            {t('admin.prospection.associatedCampaigns.subtitle')}
          </p>
        </div>
        {showInternalToggle ? (
          <button
            id={buttonId}
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={handleToggle}
            className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {open
              ? t('admin.prospection.associatedCampaigns.hide')
              : t('admin.prospection.associatedCampaigns.show')}
          </button>
        ) : null}
      </div>

      <div
        id={panelId}
        role="region"
        aria-labelledby={showInternalToggle ? buttonId : undefined}
        className={panelVisible ? 'mt-4' : 'hidden'}
        hidden={!panelVisible}
      >
        {keepMounted || panelVisible ? (
          <>
          {status === 'loading' ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              {t('admin.prospection.associatedCampaigns.loading')}
            </p>
          ) : null}

          {status === 'empty' ? (
            <>
              <p className="text-sm text-slate-600">{t('admin.prospection.associatedCampaigns.empty')}</p>
              {orderingCoverage === 'snapshotCreatedAt_present_only' ? (
                <p className="mt-2 text-xs text-slate-500">
                  {t('admin.prospection.associatedCampaigns.coverage.snapshotCreatedAt')}
                </p>
              ) : null}
            </>
          ) : null}

          {status === 'ready' && emptyWithMore && entries.length === 0 ? (
            <>
              <p className="text-sm text-slate-600" aria-live="polite">
                {t('admin.prospection.associatedCampaigns.emptyMore')}
              </p>
              {orderingCoverage === 'snapshotCreatedAt_present_only' ? (
                <p className="mt-2 text-xs text-slate-500">
                  {t('admin.prospection.associatedCampaigns.coverage.snapshotCreatedAt')}
                </p>
              ) : null}
            </>
          ) : null}

          {status === 'error' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p>{errorMessage}</p>
              <button
                type="button"
                onClick={() => loadCampaigns()}
                className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('admin.prospection.retry')}
              </button>
            </div>
          ) : null}

          {entries.length > 0 && (status === 'ready' || loadMoreFailed) ? (
            <>
              <p className="mb-3 text-xs text-slate-500">
                {t('admin.prospection.associatedCampaigns.disclaimer')}
              </p>
              {orderingCoverage === 'snapshotCreatedAt_present_only' ? (
                <p className="mb-3 text-xs text-slate-500">
                  {t('admin.prospection.associatedCampaigns.coverage.snapshotCreatedAt')}
                </p>
              ) : null}
              <ul className="space-y-3">
                {entries.map((entry) => {
                  const href = associatedCampaignOpenPath(entry.campaignId);
                  return (
                    <li
                      key={entry.campaignId}
                      className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 p-3"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-2">
                          <p className="break-words text-sm font-semibold text-slate-900">
                            {associatedCampaignDisplayName(entry, t)}
                          </p>
                          <dl className="grid gap-2 sm:grid-cols-2">
                            <div className="min-w-0">
                              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                {t('admin.prospection.associatedCampaigns.field.createdAt')}
                              </dt>
                              <dd className="mt-0.5 break-words text-sm text-slate-800">
                                {formatAssociatedCampaignCreatedAt(entry.createdAt, language)}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                {t('admin.prospection.associatedCampaigns.field.includedAt')}
                              </dt>
                              <dd className="mt-0.5 break-words text-sm text-slate-800">
                                {formatAssociatedCampaignIncludedAt(entry.includedAt, language)}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                {t('admin.prospection.campaign.field.status')}
                              </dt>
                              <dd className="mt-0.5 break-words text-sm text-slate-800">
                                {associatedCampaignStatusLabel(entry.status, t)}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                {t('admin.prospection.campaign.field.buildStatus')}
                              </dt>
                              <dd className="mt-0.5 break-words text-sm text-slate-800">
                                {associatedCampaignBuildStatusLabel(entry.buildStatus, t)}
                              </dd>
                            </div>
                          </dl>
                        </div>
                        {href ? (
                          <Link
                            to={href}
                            className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {t('admin.prospection.associatedCampaigns.open')}
                          </Link>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {loadMoreFailed ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p>{t('admin.prospection.associatedCampaigns.loadMoreError')}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => loadCampaigns({ append: true, pageToken: nextPageToken })}
                className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
              >
                {t('admin.prospection.retry')}
              </button>
            </div>
          ) : null}

          {showContinue ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => loadCampaigns({ append: true, pageToken: nextPageToken })}
              className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {emptyWithMore && entries.length === 0
                ? t('admin.prospection.associatedCampaigns.continue')
                : t('admin.prospection.associatedCampaigns.more')}
            </button>
          ) : null}
          </>
        ) : null}
        </div>
    </section>
  );
}
